/**
 * WizardSetupReviewPanel — read-only review of Site Project Intent +
 * report setup fields resolved from Coverage Matrix / typedPreferencesDefaults.
 *
 * Shown in New Report wizard Step 2 (Review) instead of editable preference forms.
 */
import React, { useMemo, useRef, useImperativeHandle } from 'react';
import { getDocumentChecklistFor } from '../../config/documentChecklists';
import { getReportType, type ReportTypeId } from '../../config/reportTypes';
import { allowsScreeningWithSourceGaps } from '../../lib/reportGeneration/screeningPolicy';
import {
  SITE_PROJECT_INTENT_ID,
  reportSetupRequirementId,
  isSetupEnabledReportType,
} from '../../config/reportSetupRequirements';
import {
  resolveReportSetupFields,
  resolveWizardPreferencesForType,
} from '../../lib/coverage/resolveReportSetupFields';
import type { ReportPrimaryField } from '../../lib/coverage/resolveReportFields';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { validatePreferencesFor } from './preferences/index';
import { FieldProvenanceChip } from './coverage/coverageMatrixUi';
import {
  RequirementInlineSetupForm,
  type RequirementInlineSetupFormHandle,
} from './coverage/RequirementInlineSetupForm';

const FIELD_ROW_CLASS =
  'grid grid-cols-1 gap-x-2 gap-y-0.5 sm:grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)_auto] sm:items-start text-[10px]';

const SCREENING_BRIEF_KEYS: ReadonlySet<string> = new Set([
  'workloadProfile', 'powerRequirement', 'powerRequirementMW', 'powerBasis', 'inServiceDate', 'priority',
]);
const SCREENING_SETUP_KEYS: ReadonlySet<string> = new Set(['firmCapacityBasisMW']);

export interface WizardSetupReviewPanelHandle {
  flushInlineSetup: () => Promise<boolean>;
}

export interface WizardSetupReviewPanelProps {
  reportType: ReportTypeId;
  site: Pick<SiteEntity, 'id' | 'name' | 'siteArea' | 'address' | 'projectIntent' | 'typedPreferencesDefaults'>;
  sed?: SiteEvidenceDocument | null;
  onOpenSiteIntent?: () => void;
  onOpenCoverageMatrix?: () => void;
  /** When false, hide per-type report setup (inline wizard form owns validation). */
  includeReportSetup?: boolean;
  /** Optional ref for flushing dirty inline setup before Generate. */
  setupFlushRef?: React.Ref<WizardSetupReviewPanelHandle>;
}

function SetupFieldStatusPill({ field }: { field: ReportPrimaryField }) {
  if (field.isEmpty) {
    return (
      <span
        data-testid="wizard-setup-field-status-empty"
        className="inline-flex items-center gap-0.5 px-1.5 py-px border border-border-subtle bg-surface-2 text-text-muted type-body uppercase tracking-widest rounded-md"
      >
        EMPTY
      </span>
    );
  }
  if (field.provenance === 'derived') {
    return <FieldProvenanceChip provenance="derived" sourceLabel={field.sourceLabel} />;
  }
  if (field.provenance === 'authored') {
    return <FieldProvenanceChip provenance="authored" sourceLabel={field.sourceLabel} />;
  }
  return (
    <FieldProvenanceChip
      provenance={field.provenance === 'missing' ? 'unknown' : field.provenance}
      sourceLabel={field.sourceLabel}
    />
  );
}

function SetupFieldList({
  fields,
  requiredKeys,
}: {
  fields: ReportPrimaryField[];
  requiredKeys?: ReadonlySet<string>;
}) {
  if (fields.length === 0) {
    return <p className="text-[10px] text-text-muted italic">No values configured yet.</p>;
  }
  return (
    <ul className="space-y-2">
      {fields.map(f => {
        const showRequired = f.required || requiredKeys?.has(f.key);
        return (
        <li
          key={f.key}
          id={`wizard-setup-field-row-${f.key}`}
          className={`${FIELD_ROW_CLASS} space-y-0.5`}
          data-testid={`wizard-setup-field-${f.key}`}
        >
          <span className="text-text-muted flex flex-wrap items-center gap-1">
            {f.label}
            {showRequired && (
              <span
                className={`rounded-sm border px-1 py-px text-[9px] font-semibold uppercase tracking-wide ${
                  f.isEmpty
                    ? 'border-warning/40 bg-warning/10 text-warning'
                    : 'border-border-subtle text-text-muted'
                }`}
              >
                {f.isEmpty ? 'Required' : 'Required *'}
              </span>
            )}
          </span>
          <span
            className={`font-mono break-words min-w-0 ${f.isEmpty ? 'text-text-muted italic' : 'text-text-primary'}`}
            title={f.isEmpty ? undefined : f.displayValue}
          >
            {f.isEmpty ? 'Not set' : f.displayValue}
          </span>
          <div className="flex flex-wrap items-center gap-1 sm:justify-end">
            <SetupFieldStatusPill field={f} />
          </div>
        </li>
        );
      })}
    </ul>
  );
}

function SetupSection({
  title,
  fields,
  editLabel,
  onEdit,
  editTestId,
  sectionRef,
  requiredInlineSetup,
  optionalInlineSetup,
  optionalFieldCount,
  validationMissingLabels,
  conditionallyRequiredKeys,
  openOptionalSection,
  optionalSectionTestId = 'wizard-setup-optional-fields',
  excludeKeys,
  primaryFieldKeys,
}: {
  title: string;
  fields: ReturnType<typeof resolveReportSetupFields>['primary'];
  editLabel: string;
  onEdit?: () => void;
  editTestId: string;
  sectionRef?: React.RefObject<HTMLElement | null>;
  requiredInlineSetup?: React.ReactNode;
  optionalInlineSetup?: React.ReactNode;
  optionalFieldCount?: number;
  validationMissingLabels?: string[];
  conditionallyRequiredKeys?: ReadonlySet<string>;
  openOptionalSection?: boolean;
  /** Distinct test id per section so the intent + report-setup disclosures are
   *  individually addressable (P0-1 added a second disclosure). */
  optionalSectionTestId?: string;
  /** Field keys owned by another section (the per-type report-setup section is
   *  the canonical home for the BTM decision facts). Excluding them here keeps
   *  each setup-field row from rendering twice — once in the intent section's
   *  optional disclosure AND once in the report-setup section's main list. */
  excludeKeys?: ReadonlySet<string>;
  /** Screening shows its brief first without declaring those fields mandatory. */
  primaryFieldKeys?: ReadonlySet<string>;
}) {
  const visibleFields = excludeKeys && excludeKeys.size > 0
    ? fields.filter(f => !excludeKeys.has(f.key))
    : fields;
  const requiredFields = visibleFields.filter(f => f.required);
  const optionalFields = visibleFields.filter(f => !f.required);
  const missingSchemaRequired = requiredFields.filter(f => f.isEmpty);
  // The main field list renders the REQUIRED fields, OR — when the schema has no
  // required fields at all (e.g. the feasibility / supply-chain "decision facts"
  // setup, every row optional) — it falls back to showing ALL fields so the
  // review is not an empty collapsed disclosure. In that fallback case the
  // optional-fields disclosure would otherwise re-render the SAME rows the main
  // list already showed, so every setup field appeared TWICE (dup bug). Derive
  // the rows the main list will show and exclude them from the disclosure so each
  // field row renders exactly once within a section.
  const mainFields = primaryFieldKeys
    ? visibleFields.filter(f => primaryFieldKeys.has(f.key))
    : requiredFields.length > 0 ? requiredFields : visibleFields;
  const mainFieldKeys = new Set(mainFields.map(f => f.key));
  const disclosureFields = optionalFields.filter(f => !mainFieldKeys.has(f.key));
  const missingConditional = disclosureFields.filter(
    f => conditionallyRequiredKeys?.has(f.key) && f.isEmpty,
  );
  // Show the optional disclosure only when there are genuinely-extra optional
  // fields NOT already in the main list. `optionalFieldCount` (caller-supplied)
  // gates whether the section *can* have a disclosure at all; `disclosureFields`
  // gates whether it has anything left to show after de-duping against the main
  // list.
  const showOptionalSection =
    optionalFieldCount !== undefined
    && optionalFieldCount > 0
    && disclosureFields.length > 0;
  const requiredKeySet = conditionallyRequiredKeys;

  return (
    <section ref={sectionRef} className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">{title}</h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-[10px] font-semibold text-primary hover:underline shrink-0"
            data-testid={editTestId}
          >
            {editLabel}
          </button>
        )}
      </div>

      {mainFields.length > 0 ? (
        <SetupFieldList fields={mainFields} requiredKeys={requiredKeySet} />
      ) : (
        <p className="text-[10px] text-text-muted italic">No values configured yet.</p>
      )}

      {(missingSchemaRequired.length > 0 || (validationMissingLabels?.length ?? 0) > 0) && (
        <ul
          className="space-y-0.5 rounded-sm border border-warning/30 bg-warning/5 p-2 text-[10px]"
          data-testid={`${editTestId}-missing-required`}
        >
          {missingSchemaRequired.map(f => (
            <li key={f.key} className="text-warning">
              Required: {f.label}
            </li>
          ))}
          {(validationMissingLabels ?? []).map(label => (
            <li key={label} className="text-warning">
              Required: {label}
            </li>
          ))}
        </ul>
      )}

      {requiredInlineSetup}

      {showOptionalSection && (
        <details
          open={openOptionalSection}
          className="rounded-sm border border-border-subtle bg-surface-1/40"
          data-testid={optionalSectionTestId}
        >
          <summary className="cursor-pointer px-2.5 py-2 text-[10px] font-semibold text-text-secondary hover:bg-surface-1/80">
            Additional setup fields (optional, derived from your site)
            <span className="ml-1 font-normal text-text-muted">
              — Show {disclosureFields.length} optional field{disclosureFields.length === 1 ? '' : 's'}
              {missingConditional.length > 0
                ? ` (${missingConditional.length} need${missingConditional.length === 1 ? 's' : ''} input)`
                : ''}
            </span>
          </summary>
          <div className="space-y-2 border-t border-border-subtle px-2.5 py-2">
            <SetupFieldList fields={disclosureFields} requiredKeys={requiredKeySet} />
          </div>
        </details>
      )}

      {/* The editable "Edit report setup" inline form is an affordance distinct
          from the read-only optional disclosure: it must render even when the
          read-only review already shows every field in the main list (i.e. the
          all-optional decision-facts setup), so it lives outside the disclosure.
          The read-only rows above are the review; this is the edit path. */}
      {optionalInlineSetup}
    </section>
  );
}

export const WizardSetupReviewPanel: React.FC<WizardSetupReviewPanelProps> = ({
  reportType,
  site,
  sed,
  onOpenSiteIntent,
  onOpenCoverageMatrix,
  includeReportSetup = true,
  setupFlushRef,
}) => {
  const typeLabel = getReportType(reportType).shortLabel;
  const screening = allowsScreeningWithSourceGaps(reportType);
  const setupEnabled = includeReportSetup && isSetupEnabledReportType(reportType);
  const requiredInlineRef = useRef<RequirementInlineSetupFormHandle>(null);
  const optionalInlineRef = useRef<RequirementInlineSetupFormHandle>(null);

  useImperativeHandle(setupFlushRef, () => ({
    flushInlineSetup: async () => {
      const requiredOk = await requiredInlineRef.current?.saveIfDirty() ?? true;
      const optionalOk = await optionalInlineRef.current?.saveIfDirty() ?? true;
      return requiredOk && optionalOk;
    },
  }));

  const { intentItem, setupItem, intentResolved, setupResolved } = useMemo(() => {
    const checklist = getDocumentChecklistFor(reportType);
    const intent = checklist.find(i => i.id === SITE_PROJECT_INTENT_ID) ?? null;
    const setup = setupEnabled
      ? checklist.find(i => i.id === reportSetupRequirementId(reportType)) ?? null
      : null;

    const resolveArgs = {
      reportType,
      projectIntent: site.projectIntent,
      typedPreferences: site.typedPreferencesDefaults,
      site: site as SiteEntity,
      sed,
    };

    const intentFields = intent
      ? resolveReportSetupFields({ requirement: intent, ...resolveArgs })
      : null;
    const setupFields = setup
      ? resolveReportSetupFields({ requirement: setup, ...resolveArgs })
      : null;

    return {
      intentItem: intent,
      setupItem: setup,
      intentResolved: intentFields,
      setupResolved: setupFields,
    };
  }, [reportType, site, setupEnabled, sed]);

  const handleOpenIntent = onOpenSiteIntent;
  const handleOpenCoverage = onOpenCoverageMatrix;
  const setupSectionRef = useRef<HTMLElement>(null);

  const setupValidation = useMemo(() => {
    if (!setupEnabled) return { valid: true, missingRequired: [] as string[], missingRequiredKeys: [] as string[] };
    const prefs = resolveWizardPreferencesForType(reportType, site as SiteEntity, sed) ?? {};
    return validatePreferencesFor(reportType, prefs as never);
  }, [reportType, site, setupEnabled, sed]);

  const conditionallyRequiredKeys = useMemo(
    () => new Set(setupValidation.missingRequiredKeys ?? []),
    [setupValidation.missingRequiredKeys],
  );

  const missingSetupFields = useMemo(() => {
    if (!setupResolved) return [];
    const keys = new Set<string>([
      ...setupResolved.primary.filter(f => f.required && f.isEmpty).map(f => f.key),
      ...(setupValidation.missingRequiredKeys ?? []),
    ]);
    return setupResolved.primary.filter(f => keys.has(f.key));
  }, [setupResolved, setupValidation.missingRequiredKeys]);

  const optionalSetupFields = useMemo(
    () => setupResolved?.primary.filter(f => !f.required && (reportType !== 'feasibility' || SCREENING_SETUP_KEYS.has(f.key))) ?? [],
    [setupResolved, reportType],
  );

  // Keys owned by the per-type report-setup section. That section is the
  // canonical home for the BTM decision facts (btmStrategy, datacenterTierTarget,
  // redundancyClass, pueTarget, carbonCommitment, …) — several of which are ALSO
  // raw Site-Project-Intent schema fields. Without excluding them from the intent
  // section, those rows render TWICE: once in the intent "Additional setup fields"
  // disclosure AND once in the report-setup section's main list (the dup bug).
  const setupFieldKeys = useMemo(
    () => new Set((setupResolved?.primary ?? []).map(f => f.key)),
    [setupResolved],
  );

  // P0-1 — the Site-Project-Intent section previously rendered ONLY the two
  // `required` schema rows and never passed `optionalFieldCount`, so the
  // optional-fields disclosure could not appear and every other intent field
  // (BTM cooling axes, expanded profile, attestations) was silently dropped
  // before generation. Count the non-required intent fields so the disclosure
  // renders (mirrors the per-report-setup section below). Exclude the
  // report-setup-owned keys so the "Show N optional fields" count matches the
  // de-duped rows the disclosure actually shows.
  const optionalIntentFieldCount = useMemo(
    () => intentResolved?.primary.filter(
      f => !f.required && !setupFieldKeys.has(f.key),
    ).length ?? 0,
    [intentResolved, setupFieldKeys],
  );

  const openOptionalSection = useMemo(
    () => optionalSetupFields.some(
      f => conditionallyRequiredKeys.has(f.key) && f.isEmpty,
    ),
    [optionalSetupFields, conditionallyRequiredKeys],
  );

  const inlineFieldKeysNeedingInput = useMemo(
    () => missingSetupFields.map(f => f.key),
    [missingSetupFields],
  );

  const scrollToMissingSetup = () => {
    const firstKey = missingSetupFields[0]?.key;
    const target = firstKey
      ? document.getElementById(`wizard-setup-field-row-${firstKey}`)
      : setupSectionRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const inlineSetupCommon = setupItem && setupItem.kind === 'report-setup' ? {
    siteId: site.id,
    site,
    item: setupItem,
    reportType,
    typedPreferences: site.typedPreferencesDefaults,
    projectIntent: site.projectIntent,
    sed,
  } : null;

  return (
    <div className="space-y-5" data-testid="wizard-setup-review">
      <p className="text-[10px] text-text-muted leading-snug">
        Add the inputs needed for this report below. Shared engineering and commercial inputs live in{' '}
        <strong>Project Intent → Design & report assumptions</strong>.
        {screening && ' Screening compares power and cooling options and recommends an approach. No technology choice is required; unknown details and evidence gaps remain disclosed.'}
      </p>

      {missingSetupFields.length > 0 && (
        <button
          type="button"
          onClick={scrollToMissingSetup}
          className="w-full text-left px-3 py-2 bg-warning/10 rounded-md border border-warning/30 text-warning text-[10px] hover:bg-warning/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-warning/40"
          data-testid="wizard-setup-incomplete-banner"
        >
          Required fields below — fill them inline or click &quot;Edit in Coverage Matrix →&quot; to use the full editor.
        </button>
      )}

      {intentItem && intentResolved && (
        <SetupSection
          title="Site Project Intent"
          fields={screening
            ? intentResolved.primary.filter(f => SCREENING_BRIEF_KEYS.has(f.key)).map(f => ({ ...f, required: false }))
            : intentResolved.primary}
          primaryFieldKeys={screening ? SCREENING_BRIEF_KEYS : undefined}
          editLabel="Edit Site Project Intent →"
          onEdit={handleOpenIntent}
          editTestId="wizard-edit-site-intent"
          optionalFieldCount={optionalIntentFieldCount}
          optionalSectionTestId="wizard-setup-intent-optional-fields"
          excludeKeys={setupFieldKeys}
        />
      )}

      {setupItem && setupResolved && inlineSetupCommon && (
        <SetupSection
          title={`${typeLabel} report setup`}
          fields={reportType === 'feasibility' ? setupResolved.primary.filter(f => SCREENING_SETUP_KEYS.has(f.key)) : setupResolved.primary}
          primaryFieldKeys={reportType === 'feasibility' ? SCREENING_SETUP_KEYS : undefined}
          editLabel="Edit in Coverage Matrix →"
          onEdit={handleOpenCoverage}
          editTestId="wizard-edit-coverage-matrix"
          sectionRef={setupSectionRef}
          optionalFieldCount={optionalSetupFields.length}
          validationMissingLabels={
            setupValidation.missingRequired?.filter(label =>
              !missingSetupFields.some(f => f.label === label),
            )
          }
          conditionallyRequiredKeys={conditionallyRequiredKeys}
          openOptionalSection={openOptionalSection}
          requiredInlineSetup={
            inlineFieldKeysNeedingInput.some(k =>
              setupResolved.primary.find(f => f.key === k && f.required),
            ) ? (
              <RequirementInlineSetupForm
                ref={requiredInlineRef}
                {...inlineSetupCommon}
                fieldKeys={inlineFieldKeysNeedingInput.filter(k =>
                  setupResolved.primary.find(f => f.key === k && f.required),
                )}
                testId="coverage-requirement-inline-setup-required"
              />
            ) : undefined
          }
          optionalInlineSetup={
            optionalSetupFields.length > 0 ? (
              <RequirementInlineSetupForm
                ref={optionalInlineRef}
                {...inlineSetupCommon}
                fieldKeys={optionalSetupFields.map(f => f.key)}
                testId="coverage-requirement-inline-setup-optional"
              />
            ) : undefined
          }
        />
      )}
    </div>
  );
};
